import React from "react";
import { useTranslation } from "react-i18next";
import { TrophyIcon } from "@heroicons/react/24/outline";
import { StarIcon } from "@heroicons/react/24/solid";
import { Profile } from "../../../types/types";
import { formatText } from "../../../utils/common/formatUtils";
import CountryFlag from "../../common/countryFlag";

interface ProfileIdentityProps {
  member: Profile;
  reviewCount?: number;
  /** "hero" = on top of cover image, "sticky" = compact blue band while scrolling */
  variant?: "hero" | "sticky";
}

const VerifiedBadge: React.FC<{ label: string }> = ({ label }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 48 48"
    className="relative top-1 w-4 h-4 lg:w-6 lg:h-6 shrink-0"
    aria-label={label}
  >
    <polygon
      fill="#42a5f5"
      points="29.62,3 33.053,8.308 39.367,8.624 39.686,14.937 44.997,18.367 42.116,23.995 45,29.62 39.692,33.053 39.376,39.367 33.063,39.686 29.633,44.997 24.005,42.116 18.38,45 14.947,39.692 8.633,39.376 8.314,33.063 3.003,29.633 5.884,24.005 3,18.38 8.308,14.947 8.624,8.633 14.937,8.314 18.367,3.003 23.995,5.884"
    />
    <polygon
      fill="#fff"
      points="21.396,31.255 14.899,24.76 17.021,22.639 21.428,27.046 30.996,17.772 33.084,19.926"
    />
  </svg>
);

/**
 * Name / nickname / verified / trophy on the left,
 * country + rating on the right. Same sizes for hero and sticky band
 * so the sticky band looks exactly like the hero text.
 */
const ProfileIdentity: React.FC<ProfileIdentityProps> = ({
  member,
  reviewCount = 0,
  variant = "hero",
}) => {
  const { t } = useTranslation("publicProfile");

  const fullName = `${formatText(member.firstName) || ""} ${
    formatText(member.lastName) || ""
  }`.trim();
  const nickname = member.fighter?.nickname?.trim();
  const orgName = member.manager?.organizationName?.trim();
  const roundedRating =
    member.rating != null ? Math.round(Number(member.rating)) : 0;
  const inactiveStar =
    variant === "sticky" ? "text-white/50" : "text-neutral-400";

  // For organisation/manager profiles, show org name as primary display name.
  // For fighter profiles, show nickname (if any) as primary, fullName as secondary.
  const primaryName = orgName || nickname || fullName;
  const secondaryName = orgName ? fullName : nickname ? fullName : null;

  return (
    <div className="flex flex-row justify-between items-end gap-2.5 w-full">
      <div className="flex min-w-0 flex-col justify-center items-start lg:flex-1">
        <div className="self-stretch flex min-w-0 items-center gap-1 flex-nowrap">
          <span className="text-white text-lg lg:text-6xl font-bold leading-7 lg:leading-[60px] whitespace-nowrap truncate">
            {primaryName}
          </span>
          {!orgName && !nickname && member.verified && (
            <VerifiedBadge label={t("verified")} />
          )}
          {member.fighter?.titleHolder && (
            <TrophyIcon className="w-4 h-4 lg:w-6 lg:h-6 text-amber-400 shrink-0" />
          )}
        </div>
        {secondaryName && (
          <div className="self-stretch flex min-w-0 items-center gap-1">
            <span className="text-white text-base lg:text-3xl font-normal leading-6 lg:leading-9 truncate">
              {secondaryName}
            </span>
            {member.verified && <VerifiedBadge label={t("verified")} />}
          </div>
        )}
      </div>

      <div className="flex flex-col justify-center items-end gap-1.5 shrink-0">
        {member.residenceCountry && (
          <div className="flex items-center gap-1.5 lg:gap-2 text-white text-sm lg:text-base leading-5 lg:leading-6">
            <CountryFlag
              countryName={member.residenceCountry}
              size={16}
              className="shadow-sm rounded-full overflow-hidden"
            />
            <span>{member.residenceCountry}</span>
          </div>
        )}
        {member.rating != null && (
          <div className="flex items-center gap-1 text-white text-sm lg:text-base leading-5 lg:leading-6">
            {[...Array(5)].map((_, i) => (
              <StarIcon
                key={i}
                className={`w-3.5 h-3.5 lg:w-6 lg:h-6 ${
                  i < roundedRating ? "text-yellow-400" : inactiveStar
                }`}
              />
            ))}
            <span className="ml-1">
              {reviewCount} ({t("reviews", { defaultValue: "Reviews" })})
            </span>
            <span className="hidden lg:inline ml-2 font-medium">
              {Number(member.rating).toFixed(1)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileIdentity;
